/**
 * src/lib/i18n/config.ts
 *
 * i18n routing config — Next.js middleware ve navigation katmanının ihtiyacı
 * olan locale bilgisini `siteSettings.locales` registry'sinden derler.
 *
 * β.2.5 ile **tek kaynak siteSettings**. Önceki sürümde Domain'in
 * `SUPPORTED_LOCALES` ve `DEFAULT_LOCALE` const'ları "filter" rolü oynuyordu
 * (registry'deki bir code Domain literal'inde yoksa devre dışı kalıyordu).
 * Artık registry'nin kendisi authoritative; Domain `Locale = string` ile
 * runtime registry'ye hizalandı.
 *
 * Build-time şu an `data/site-settings.ts` hardcoded; β.4'te DB tablosuna
 * (`site_settings` JSONB) taşındığında bu modülün kaynağı da DB'den okuyan
 * async helper'a evrilir (`getValidLocales` zaten var, sayfa içinde tercih edilir).
 *
 * Bu modül **module-scope const evaluation** kullanıyor — middleware client/edge
 * runtime'da async helper çalıştıramayacağı için sync access şart.
 */

import { siteSettings } from '@/data/site-settings'
import type { Locale } from '@/domain/value-objects/locale'

export type { Locale }

/**
 * Tüm pre-listed locale code'ları — middleware ve next-intl routing'in
 * `locales` parametresi. `enabled` filter UYGULANMAZ; build-time'da disabled
 * olan diller de routing'e dahil olur ki admin runtime'da enable edince
 * `/de/`, `/ar/` URL'leri çalışsın (yeniden build gerekmez).
 *
 * Runtime enabled check: (public)/[locale]/layout.tsx içinde `getValidLocales()`
 * async helper ile yapılır — disabled locale URL'i `notFound()` döner.
 *
 * Boş ise build/start'ta hata atılır — i18n config'siz site çalışmaz.
 */
export const SUPPORTED_LOCALES: readonly Locale[] = siteSettings.locales.map(
  (l) => l.code,
)

if (SUPPORTED_LOCALES.length === 0) {
  throw new Error(
    '[i18n/config] siteSettings.locales boş — site i18n config\'siz başlatılamaz',
  )
}

/**
 * Yayında olan locale code'ları — sadece `enabled: true` olanlar.
 *
 * `SUPPORTED_LOCALES`'tan farklı: o routing/middleware/locale-switcher için
 * tüm 16 dili tutar (admin runtime'da yeni dili enable edince /de/, /ar/
 * URL'leri rebuild olmadan çalışsın diye). Bu ise **sitemap.xml** ve
 * **hreflang alternate generator**'ı için kullanılır — Google'a "şu dilde
 * gerçek içeriğim var" diyen kanıt.
 *
 * Yanlış filter (tüm 16 dili sitemap'e koymak) hayalet URL'ler doğurur:
 * `/ar/blog`, `/de/hizmetler` → 404 → Google "submitted not indexed" yığını.
 *
 * Build-time evaluation; admin'de bir dil toggle edildiğinde rebuild gerekli
 * (sitemap ISR / static).
 */
export const ENABLED_LOCALES: readonly Locale[] = siteSettings.locales
  .filter((l) => l.enabled)
  .map((l) => l.code)

if (ENABLED_LOCALES.length === 0) {
  throw new Error(
    '[i18n/config] siteSettings.locales içinde enabled:true hiç locale yok — yayında dil olmadan site açılamaz',
  )
}

/**
 * Default locale resolution sırası:
 *  1) `siteSettings.locales[]` içinde `isDefault: true` ve `enabled: true` olan
 *  2) `SUPPORTED_LOCALES[0]` (fallback — yukarıdaki kayıt yoksa)
 *
 * Boş registry edge case'i yukarıdaki throw ile zaten yakalanır; bu noktada
 * `SUPPORTED_LOCALES` en az bir locale içerir.
 */
function resolveDefaultLocale(): Locale {
  const fromData = siteSettings.locales.find((l) => l.isDefault && l.enabled)
  if (fromData) return fromData.code

  // SUPPORTED_LOCALES boşsa yukarıdaki throw zaten patlardı; bu satıra ulaşılırsa
  // en az bir locale garanti vardır.
  return SUPPORTED_LOCALES[0]!
}

export const DEFAULT_LOCALE: Locale = resolveDefaultLocale()

/**
 * URL path prefix modu — next-intl `localePrefix` setting'i.
 * 'always' = kök `/` her zaman `/<default-locale>`a redirect olur.
 */
export const LOCALE_PREFIX_MODE = 'always' as const

/**
 * Locale switcher UI'da gösterilecek native isim eşlemesi (örn. `tr → 'Türkçe'`).
 * `siteSettings.locales[].nativeLabel`'dan derlenir; switcher component'leri
 * burayı sync olarak okuyup label render eder.
 *
 * `enabled` filter UYGULANMAZ — tüm 15 dil için label tutulur (build-time const).
 * Switcher'da hangi dilin gösterileceği runtime'da `getValidLocales()` ile
 * filtrelenir; bu map sadece label lookup için sözlük rolünde.
 */
export const LOCALE_LABELS: Record<Locale, string> = Object.fromEntries(
  siteSettings.locales.map((l) => [l.code, l.nativeLabel]),
)
